"use client"

import { getAuthHeader } from "../utils/auth"; // ★ 1. 認証ヘッダー取得関数をインポート
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { englishStatusLabel, formatJstDateTime, isEnglishOperatorMode } from "../utils/operatorMode";

// --- SVG Icons ---
const DownloadIcon = () => ( <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg> );
const ChevronDownIcon = () => ( <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg> );
const ChevronUpIcon = () => ( <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg> );
const ScreenshotIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>;
const InfoIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>;
const FilterIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>;
const LockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-slate-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>;
const LoadingSpinner = () => (
    <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);
// ★ モーダル用にCloseIconを追加
const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;


// --- Constants ---
const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;
const ITEMS_PER_PAGE = 50;


// --- Helper Functions ---
const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = (`0${d.getMonth() + 1}`).slice(-2);
    const day = (`0${d.getDate()}`).slice(-2);
    return `${year}-${month}-${day}`;
};

// --- UI Components ---
function StatsDashboard({ stats, isEnglishBeta = false }) {
    if (!stats || stats.total === 0) {
        return (
            <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                <p className="text-lg text-gray-700">{isEnglishBeta ? "No execution history for the selected period." : "指定された期間の送信履歴はありません。"}</p>
            </div>
        );
    }
    const { success, needs_review, fail, irrelevant, total } = stats;
    const approachTotal = success + needs_review + fail;
    const successRate = approachTotal > 0 ? (((success + needs_review) / approachTotal) * 100).toFixed(1) : 0;
    const data = [
        { name: isEnglishBeta ? 'Submitted' : '成功', value: success, color: '#10B981' },
        { name: isEnglishBeta ? 'Needs Review' : '要確認', value: needs_review, color: '#F59E0B' },
        { name: isEnglishBeta ? 'Failed' : '失敗', value: fail, color: '#EF4444' },
        { name: isEnglishBeta ? 'Not Eligible' : '対象外', value: irrelevant, color: '#6B7280' },
    ];
    return (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">{isEnglishBeta ? "Execution Outcome Summary" : "送信結果サマリー"}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-1 flex flex-col items-center justify-center">
                    <PieChart data={data} />
                </div>
                <div className="md:col-span-2 grid grid-cols-2 gap-4">
                    <StatCard title={isEnglishBeta ? "Total attempts" : "総試行回数"} value={total.toLocaleString()} />
                    <StatCard title={isEnglishBeta ? "Eligible outcome rate" : "有効アプローチ成功率"} value={`${successRate}%`} tooltip={isEnglishBeta ? "Submitted + Needs Review over eligible attempts." : "「対象外」を除いたアプローチの成功率（「要確認」を含む）です。"} />
                    <StatCard title={isEnglishBeta ? "Submitted" : "✅ 成功"} value={success.toLocaleString()} color="text-green-600" />
                    <StatCard title={isEnglishBeta ? "Needs Review" : "⚠️ 要確認"} value={needs_review.toLocaleString()} color="text-yellow-600" />
                    <StatCard title={isEnglishBeta ? "Failed" : "❌ 失敗"} value={fail.toLocaleString()} color="text-red-600" />
                    <StatCard title={isEnglishBeta ? "Not Eligible" : "-- 対象外"} value={irrelevant.toLocaleString()} color="text-gray-600" />
                </div>
            </div>
        </div>
    );
}

const PieChart = ({ data }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    if (total === 0) return <div className="w-40 h-40 bg-gray-200 rounded-full flex items-center justify-center text-gray-500">No data</div>;

    let cumulative = 0;
    const segments = data.map(item => {
        const percentage = (item.value / total) * 100;
        const segment = { ...item, percentage, startAngle: (cumulative / total) * 360, endAngle: ((cumulative + item.value) / total) * 360 };
        cumulative += item.value;
        return segment;
    });

    const getCoord = (percent, radius = 50) => {
        const x = 50 + radius * Math.cos(2 * Math.PI * percent);
        const y = 50 + radius * Math.sin(2 * Math.PI * percent);
        return [x, y];
    };

    return (
        <svg viewBox="0 0 100 100" className="w-40 h-40">
            {segments.map((segment, i) => {
                const [startX, startY] = getCoord(segment.startAngle / 360);
                const [endX, endY] = getCoord(segment.endAngle / 360);
                const largeArcFlag = segment.percentage > 50 ? 1 : 0;
                const pathData = `M 50,50 L ${startX},${startY} A 50,50 0 ${largeArcFlag},1 ${endX},${endY} Z`;
                return <path key={i} d={pathData} fill={segment.color} />;
            })}
        </svg>
    );
};

const StatCard = ({ title, value, color = 'text-gray-900', tooltip }) => (
    <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center">
            <h3 className="text-sm font-medium text-gray-500">{title}</h3>
            {tooltip && (
                <div className="relative ml-2 group">
                    <InfoIcon />
                    <div className="absolute bottom-full mb-2 w-48 p-2 text-xs text-white bg-gray-800 rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">{tooltip}</div>
                </div>
            )}
        </div>
        <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
    </div>
);

function Pagination({ currentPage, totalPages, onPageChange, isLoading, disabled, isEnglishBeta = false }) {
    if (totalPages <= 1) return null;
    const labels = isEnglishBeta
        ? { first: "First", previous: "Previous", next: "Next", last: "Last" }
        : { first: "最初", previous: "前へ", next: "次へ", last: "最後" };

    return (
        <div className="flex flex-wrap items-center justify-center gap-2">
            <button onClick={() => onPageChange(1)} disabled={currentPage === 1 || isLoading || disabled} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">{labels.first}</button>
            <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading || disabled} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">{labels.previous}</button>
            <span className="text-sm text-gray-700">{currentPage} / {totalPages}</span>
            <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading || disabled} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">{labels.next}</button>
            <button onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages || isLoading || disabled} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">{labels.last}</button>
        </div>
    );
}

function ScreenshotModal({ url, onClose }) {
    useEffect(() => {
        const handleKeyDown = (event) => { if (event.key === 'Escape') { onClose(); } };
        window.addEventListener('keydown', handleKeyDown);
        return () => { window.removeEventListener('keydown', handleKeyDown); };
    }, [onClose]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={onClose}>
            <div className="p-2 bg-white rounded-lg shadow-xl max-w-4xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
                <img src={url} alt="Screenshot" className="max-w-full max-h-[85vh] object-contain" />
                <button onClick={onClose} className="absolute top-2 right-4 text-white text-4xl font-bold hover:text-gray-300 transition-colors">&times;</button>
            </div>
        </div>
    );
}
const StatusBadge = ({ status, errorDetails = "", isEnglishBeta = false }) => {
    const getDisplayInfo = () => {
        const s = (status || "").toLowerCase();
        const details = (errorDetails || "").toLowerCase();
        if (isEnglishBeta) {
            const label = englishStatusLabel(s, details);
            if (label === "Submitted") return { text: label, className: 'bg-green-100 text-green-800' };
            if (label === "Needs Review") return { text: label, className: 'bg-yellow-100 text-yellow-800' };
            if (label === "Failed") return { text: label, className: 'bg-red-100 text-red-800' };
            if (label === "Suppressed") return { text: label, className: 'bg-purple-100 text-purple-800' };
            return { text: label, className: 'bg-gray-100 text-gray-600' };
        }
        if (s === 'success') return { text: '✅ 成功', className: 'bg-green-100 text-green-800' };
        if (s === 'needs_review') return { text: '⚠️ 要確認', className: 'bg-yellow-100 text-yellow-800' };
        if (s === 'no_form_found' || details.includes('フォームを発見できませんでした') || details.includes('no_form_found') || details.includes('予約フォーム') || details.includes('応募フォーム') || s === 'dead_url' || details.includes('no details available')) {
            return { text: '-- 対象外', className: 'bg-gray-100 text-gray-600' };
        }
        if (s === 'fail' || s === 'logic_failed' || s === 'gpt_failed') return { text: '❌ 失敗', className: 'bg-red-100 text-red-800' };
        return { text: '-- 対象外', className: 'bg-gray-100 text-gray-600' };
    };
    const { text, className } = getDisplayInfo();
    return <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${className}`}>{text}</span>;
};

// ★ アップグレードモーダルコンポーネントを新設
function UpgradeModal({ onClose, user, accountPath = '/account' }) {
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white p-8 rounded-lg shadow-2xl border w-full max-w-md text-center relative"
        onClick={e => e.stopPropagation()}
      >
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="閉じる"
        >
          <CloseIcon />
        </button>
        
        <LockIcon />
        
        <h3 className="text-xl font-bold text-slate-800 mt-2">
          この機能は有料プランでご利用いただけます
        </h3>
        <p className="text-slate-600 mt-4">
          全期間の履歴閲覧、ページ遷移、CSVダウンロード、案件管理の全機能を利用するには、プランのアップグレードが必要です。
        </p>
        {/* 🚀 修正: OEMユーザーかどうかでリンクを隠す */}
        {!user?.oem_id ? (
          <Link 
            to={accountPath}
            className="mt-8 inline-block bg-green-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-green-700 transition-colors w-full"
          >
            🚀 プランをアップグレードする
          </Link>
        ) : (
          <div className="mt-8 p-4 bg-blue-50 text-blue-700 font-bold rounded-xl border border-blue-100 text-center">
            アップグレードについては、ご契約先の担当者までお問い合わせください。
          </div>
        )}
      </div>
    </div>
  );
}

// --- Main Component ---
function HistoryPage({ accountPath = '/account' }) {
  const { user } = useOutletContext();
  const isEnglishBeta = isEnglishOperatorMode();
  const [summaryData, setSummaryData] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFreePlan, setIsFreePlan] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false); // ★ モーダル表示用のstateを追加

  const [dateRange, setDateRange] = useState(() => {
      const today = new Date();
      return { startDate: today, endDate: today };
  });

  const selectedClientId = useMemo(() => (user?.role === "admin" || user?.role === "admin_staff" ? localStorage.getItem("selectedClientId") || "" : user?.client_id), [user]);

  const fetchData = useCallback((pageToFetch, filters) => {
    if (!selectedClientId) {
      setSummaryData([]);
      setStats(null);
      setPagination(null);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    const startDateStr = formatDateForInput(filters.startDate);
    const endDateStr = formatDateForInput(filters.endDate);

    const params = new URLSearchParams({
        client_id: selectedClientId,
        page: pageToFetch,
        limit: ITEMS_PER_PAGE,
        startDate: startDateStr,
        endDate: endDateStr
    });

    fetch(`${API_URL}/api/history/summary?${params.toString()}`, {
        headers: getAuthHeader() // ★ 2. 認証ヘッダーを追加
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setSummaryData(data.summary || []);
          setStats(data.stats || null);
          setPagination(data.pagination || null);
        } else {
          throw new Error(data.error || "サマリー履歴の取得に失敗しました。");
        }
      })
      .catch((error) => {
        console.error("サマリー履歴の取得中にエラーが発生しました:", error);
        setSummaryData([]);
        setStats(null);
        setPagination(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedClientId]);

  useEffect(() => {
    if (!selectedClientId) {
      setIsLoading(false);
      return;
    }

    const fetchInitialData = async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/subscription-status?clientId=${selectedClientId}`, {
                headers: getAuthHeader() // ★ 2. 認証ヘッダーを追加
            });
        if (!res.ok) throw new Error(isEnglishBeta ? 'Could not load plan information.' : 'プラン情報の取得に失敗しました。');
            const data = await res.json();
            if (data.success) {
                const isFree = !data.subscription || data.subscription.plan_id_string === 'free';
                setIsFreePlan(isFree);
            } else {
                throw new Error(data.error || (isEnglishBeta ? 'Could not load plan information.' : 'プラン情報の取得に失敗しました。'));
            }
            fetchData(1, dateRange);
        } catch (error) {
            console.error("初期データの取得に失敗:", error);
            setIsLoading(false);
        }
    };

    fetchInitialData();
  }, [selectedClientId, fetchData, dateRange, isEnglishBeta]); // ★ 修正: fetchData と dateRange を依存配列に追加


  const handlePageChange = (newPage) => {
    // ★ 修正: 無料プランではページ遷移時にポップアップ
    if (isFreePlan) {
        setShowUpgradeModal(true);
        return;
    }
    if (newPage > 0 && pagination && newPage <= pagination.totalPages) {
        setCurrentPage(newPage);
        fetchData(newPage, dateRange);
    }
  };
  
  const handleFilterApply = (newDateRange) => {
    // ★ 修正: 無料プランではフィルター適用時にポップアップ
    if (isFreePlan) {
        setShowUpgradeModal(true);
        return;
    }
    setCurrentPage(1);
    setDateRange(newDateRange);
    fetchData(1, newDateRange);
  };

  const downloadCSV = () => {
    // ★ 修正: CSVは無料プランでは無効 (ポップアップ)
    if (isFreePlan) {
        setShowUpgradeModal(true);
        return;
    }
    if (!selectedClientId) return alert("クライアントを選択してください。");
    
    const startDateStr = formatDateForInput(dateRange.startDate);
    const endDateStr = formatDateForInput(dateRange.endDate);
    
    let apiUrl = `${API_URL}/api/history/summary-csv?client_id=${selectedClientId}`;
    if(startDateStr && endDateStr) {
        apiUrl += `&startDate=${startDateStr}&endDate=${endDateStr}`;
    }

    fetch(apiUrl, {
        headers: getAuthHeader() // ★ 2. 認証ヘッダーを追加
    })
      .then((res) => {
        if (!res.ok) { return res.json().then(err => { throw new Error(err.error || "CSVレポートのダウンロードに失敗しました。") }); }
        return res.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const a = document.createElement("a");
        a.href = url;
        a.download = "send_history_report.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((err) => {
          console.error("CSVダウンロードエラー:", err);
          alert(err.message);
      });
  };

  const toggleExpand = (deal_id) => {
    // ★ 修正: 無料プランの場合は詳細を開けない (ポップアップ)
    if (isFreePlan) {
        setShowUpgradeModal(true);
        return;
    }
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(deal_id)) { newSet.delete(deal_id); } 
      else { newSet.add(deal_id); }
      return newSet;
    });
  };

  if (isLoading && !stats) { // 初回ロード時のみフルスクリーンローダー
      return (
          <div className="flex items-center justify-center h-[80vh]">
              <div className="flex flex-col items-center">
                  <LoadingSpinner />
                  <p className="text-lg font-semibold text-gray-700 mt-4">{isEnglishBeta ? "Loading execution history..." : "履歴データを読み込んでいます..."}</p>
              </div>
          </div>
      );
  }

  return (
    <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{isEnglishBeta ? "Execution History" : "📋 送信履歴"}</h1>
            {isEnglishBeta && <p className="mt-2 text-sm text-gray-600">Review governed outreach outcomes. All timestamps are displayed in JST.</p>}
          </div>
          {/* --- ▼▼▼ 修正点 1 ▼▼▼ --- */}
          {/* disabled={isFreePlan} は *維持* します (CSVは無効) */}
          <button onClick={downloadCSV} disabled={isFreePlan} className="flex w-full items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 transition-colors sm:w-auto sm:self-start md:self-center disabled:bg-gray-400 disabled:cursor-not-allowed">
            <DownloadIcon /> {isEnglishBeta ? "Export CSV" : "CSVダウンロード"}
          </button>
          {/* --- ▲▲▲ 修正ここまで ▲▲▲ --- */}
        </div>
        
        {/* --- ▼▼▼ 修正点 2 ▼▼▼ --- */}
        {/* disabled={isFreePlan} は *維持* します (期間指定は無効) */}
        <DateRangeFilter onApply={handleFilterApply} disabled={isFreePlan} isEnglishBeta={isEnglishBeta} />
        {/* --- ▲▲▲ 修正ここまで ▲▲▲ --- */}
        
        <StatsDashboard stats={stats} isEnglishBeta={isEnglishBeta} />

        <div className="bg-white rounded-lg shadow-md overflow-hidden relative">
            {/* --- ▼▼▼ 修正点 3 ▼▼▼ --- */}
            {/* isFreePlan のオーバーレイブロックを削除 */}
            {/* {isFreePlan && ( ... )} */}
            {/* --- ▲▲▲ 修正ここまで ▲▲▲ --- */}

            <div className="overflow-x-auto">
              {isLoading && (
                  <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-20">
                      <LoadingSpinner />
                  </div>
              )}
              <div className="divide-y divide-gray-100 md:hidden">
                {summaryData.length > 0 ? (
                  summaryData.map((summary) => (
                    <div key={`${summary.deal_id}-mobile`} className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 break-words">{summary.company_name || "N/A"}</p>
                          <p className="mt-1 text-xs text-gray-500">{isEnglishBeta ? formatJstDateTime(summary.latest_sent_at) : new Date(summary.latest_sent_at).toLocaleString("ja-JP")}</p>
                        </div>
                        <button onClick={() => toggleExpand(summary.deal_id)} className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200">
                          {expandedRows.has(summary.deal_id) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </button>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                        <StatusBadge status={summary.latest_status} isEnglishBeta={isEnglishBeta} />
                        {summary.list_type === 'client' ? (
                          <span className="rounded-full bg-purple-50 px-2 py-1 font-semibold text-purple-700">{isEnglishBeta ? "My list" : "マイリスト"}</span>
                        ) : (
                          <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-600">{isEnglishBeta ? "SalesBreaker DB" : "弊社提供"}</span>
                        )}
                      </div>
                      <p className="mt-3 text-xs text-gray-500 break-all">{summary.staff_email || "N/A"}</p>
                      {expandedRows.has(summary.deal_id) && (
                        <div className="mt-4 rounded-lg bg-gray-50 p-3">
                          <h4 className="font-semibold text-gray-800 mb-2">{isEnglishBeta ? "Latest execution record" : "最終アプローチ記録"}</h4>
                          {summary.history && summary.history.length > 0 ? (() => {
                            const latestHistory = summary.history[0];
                            return (
                              <div key={latestHistory.id} className="rounded-md bg-white p-3 text-xs shadow-sm">
                                <p className="font-semibold">{isEnglishBeta ? formatJstDateTime(latestHistory.sent_at) : new Date(latestHistory.sent_at).toLocaleString("ja-JP")}</p>
                                <div className="mt-2"><StatusBadge status={latestHistory.status} errorDetails={latestHistory.error_details} isEnglishBeta={isEnglishBeta} /></div>
                                <p className="mt-2 text-gray-500 break-words">{latestHistory.error_details}</p>
                                <p className="mt-2"><a href={latestHistory.contact_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{latestHistory.contact_url}</a></p>
                                {latestHistory.screenshot_path && (
                                  <button onClick={() => setScreenshotUrl(latestHistory.screenshot_path)} className="mt-3 flex w-full items-center justify-center gap-2 rounded-md bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300">
                                    <ScreenshotIcon /> {isEnglishBeta ? "View" : "表示"}
                                  </button>
                                )}
                              </div>
                            );
                          })() : <p className="text-xs text-gray-500">{isEnglishBeta ? "No detailed history." : "詳細な履歴はありません。"}</p>}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-10 text-center text-sm text-gray-500">
                    {isEnglishBeta ? "No execution history matches the current filters." : "表示対象の送信履歴はありません。"}
                    <br />
                    {isEnglishBeta ? "Not Eligible outcomes may be available in CSV exports." : "（「対象外」の履歴はCSVからご確認いただけます）"}
                  </div>
                )}
              </div>
              <table className="hidden w-full text-sm text-left text-gray-600 md:table">
                <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                  <tr>
                    <th className="py-3 px-4">{isEnglishBeta ? "Latest execution time (JST)" : "最終送信日時"}</th>
                    <th className="py-3 px-4">{isEnglishBeta ? "Company" : "企業名"}</th>
                    <th className="py-3 px-4">{isEnglishBeta ? "Latest status" : "最終ステータス"}</th>
                    <th className="py-3 px-4">{isEnglishBeta ? "List source" : "リスト種別"}</th>
                    <th className="py-3 px-4">{isEnglishBeta ? "Operator" : "担当者"}</th>
                    <th className="py-3 px-4">{isEnglishBeta ? "Details" : "詳細"}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {summaryData.length > 0 ? (
                    summaryData.map((summary) => (
                      <React.Fragment key={summary.deal_id}>
                        <tr className="hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium">{isEnglishBeta ? formatJstDateTime(summary.latest_sent_at) : new Date(summary.latest_sent_at).toLocaleString("ja-JP")}</td>
                          <td className="py-3 px-4">{summary.company_name || "N/A"}</td>
                          <td className="py-3 px-4">
                            <StatusBadge status={summary.latest_status} isEnglishBeta={isEnglishBeta} />
                          </td>
                          <td className="py-3 px-4">
                            {summary.list_type === 'client' ? (<span className="font-semibold text-purple-700">{isEnglishBeta ? "My list" : "マイリスト"}</span>) : (<span className="text-gray-500">{isEnglishBeta ? "SalesBreaker DB" : "弊社提供"}</span>)}
                          </td>
                          <td className="py-3 px-4">{summary.staff_email || "N/A"}</td>
                          <td className="py-3 px-4">
                            {/* --- ▼▼▼ 修正点 4 ▼▼▼ --- */}
                            {/* disabled={isFreePlan} を *削除* します (クリックは許可) */}
                            <button onClick={() => toggleExpand(summary.deal_id)} className="p-2 text-gray-600 hover:bg-gray-200 rounded-full disabled:opacity-50 disabled:cursor-not-allowed">
                              {expandedRows.has(summary.deal_id) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </button>
                            {/* --- ▲▲▲ 修正ここまで ▲▲▲ --- */}
                          </td>
                        </tr>
                        {expandedRows.has(summary.deal_id) && (
                          <tr>
                            <td colSpan="6" className="p-4 bg-gray-50">
                              <div className="space-y-3">
                                  <h4 className="font-semibold text-gray-800 mb-2">{isEnglishBeta ? "Latest execution record" : "最終アプローチ記録"}</h4>
                                  {summary.history && summary.history.length > 0 ? (() => {
                                      const latestHistory = summary.history[0];
                                      return (
                                          <div key={latestHistory.id} className="bg-white p-3 rounded-md shadow-sm text-xs">
                                              <div className="flex justify-between items-start gap-4">
                                                  <div>
                                                      <p className="font-semibold">{isEnglishBeta ? formatJstDateTime(latestHistory.sent_at) : new Date(latestHistory.sent_at).toLocaleString("ja-JP")}</p>
                                                      <p><StatusBadge status={latestHistory.status} errorDetails={latestHistory.error_details} isEnglishBeta={isEnglishBeta} /></p>
                                                      <p className="mt-1 text-gray-500">{latestHistory.error_details}</p>
                                                      <p><a href={latestHistory.contact_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{latestHistory.contact_url}</a></p>
                                                  </div>
                                                  {latestHistory.screenshot_path && (
                                                      <button onClick={() => setScreenshotUrl(latestHistory.screenshot_path)} className="flex-shrink-0 flex items-center gap-2 bg-gray-200 text-gray-700 font-semibold py-1 px-3 rounded-md hover:bg-gray-300 text-sm">
                                                          <ScreenshotIcon /> {isEnglishBeta ? "View" : "表示"}
                                                      </button>
                                                  )}
                                              </div>
                                          </div>
                                      );
                                  })() : <p className="text-xs text-gray-500">{isEnglishBeta ? "No detailed history." : "詳細な履歴はありません。"}</p>}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr><td colSpan="6" className="text-center py-10 text-gray-500">{isEnglishBeta ? "No execution history matches the current filters." : "表示対象の送信履歴はありません。"}<br />{isEnglishBeta ? "Not Eligible outcomes may be available in CSV exports." : "（「対象外」の履歴はCSVからご確認いただけます）"}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t">
                {/* --- ▼▼▼ 修正点 5 ▼▼▼ --- */}
                {/* disabled={isFreePlan} を *削除* します (クリックは許可) */}
                {pagination && <Pagination currentPage={currentPage} totalPages={pagination.totalPages} onPageChange={handlePageChange} isLoading={isLoading} isEnglishBeta={isEnglishBeta} />}
                {/* --- ▲▲▲ 修正ここまで ▲▲▲ --- */}
            </div>
          </div>
      </div>
      
      {screenshotUrl && <ScreenshotModal url={screenshotUrl} onClose={() => setScreenshotUrl(null)} />}
      {/* ★ 新規モーダルをここに追加 */}
      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} user={user} accountPath={accountPath} />}
    </div>
  );
}

const DateRangeFilter = ({ onApply, disabled, isEnglishBeta = false }) => {
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());

    const handleApplyClick = () => {
        onApply({ startDate, endDate });
    };

    return (
        <div className="bg-white p-4 rounded-lg shadow-md mb-6 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center">
                <label htmlFor="startDate" className="text-sm font-medium text-gray-700">{isEnglishBeta ? "Period:" : "期間:"}</label>
                <input
                    type="date"
                    id="startDate"
                    value={formatDateForInput(startDate)}
                    onChange={(e) => setStartDate(new Date(e.target.value))}
                    disabled={disabled} // 修正点: HistoryPageから渡されるdisabled(isFreePlan)を削除
                    className="w-full border-gray-300 rounded-md shadow-sm text-sm sm:w-auto disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <span className="text-gray-600">{isEnglishBeta ? "to" : "〜"}</span>
                <input
                    type="date"
                    id="endDate"
                    value={formatDateForInput(endDate)}
                    onChange={(e) => setEndDate(new Date(e.target.value))}
                    disabled={disabled} // 修正点: HistoryPageから渡されるdisabled(isFreePlan)を削除
                    className="w-full border-gray-300 rounded-md shadow-sm text-sm sm:w-auto disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
            </div>
            <button
                onClick={handleApplyClick}
                disabled={disabled} // 修正点: HistoryPageから渡されるdisabled(isFreePlan)を削除
                className="flex w-full items-center justify-center gap-2 bg-indigo-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors md:w-auto disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
                <FilterIcon /> {isEnglishBeta ? "Apply" : "表示"}
            </button>
        </div>
    );
};

export default HistoryPage;
